import { current_lang_short, apexcharts_lang_file, kFormatter } from './common.js'

// The charts of the transactions page. The page loads its data from the server, the public demo builds it in the
// browser; both draw it here, so the demo looks exactly like the product.

export function formatChartAmount(value) {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
        return '';
    }

    const normalized = Number(value);
    const fixed = normalized.toFixed(2);
    const parts = fixed.split('.');
    const integerPartWithSpaces = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${integerPartWithSpaces}.${parts[1]}`;
}

const monthNamesByLang = {
    uk: ['Січ', 'Лют', 'Бер', 'Кві', 'Тра', 'Чер', 'Лип', 'Сер', 'Вер', 'Жов', 'Лис', 'Гру'],
    en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    es: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
    it: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'],
    pl: ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'],
    de: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
};

export function formatChartDate(value, granularity) {
    if (value == null) {
        return '';
    }

    let date;
    const unixMs = Number(value);
    if (!Number.isNaN(unixMs) && unixMs > 0) {
        date = new Date(unixMs);
    } else {
        date = new Date(value);
    }

    if (isNaN(date.getTime())) {
        return String(value);
    }

    const langCode = (current_lang_short() || 'en').toLowerCase();
    const months = monthNamesByLang[langCode] || monthNamesByLang.en;
    const month = months[date.getUTCMonth()];
    const year = date.getUTCFullYear();
    const day = String(date.getUTCDate()).padStart(2, '0');

    if (granularity === 'year') {
        return String(year);
    }
    if (granularity === 'month') {
        return `${month} ${year}`;
    }
    return `${day} ${month}`;
}

function locales() {
    const apexLocale = apexcharts_lang_file();
    return {
        locales: apexLocale ? [apexLocale] : [],
        defaultLocale: apexLocale && apexLocale.name ? apexLocale.name : 'en'
    };
}

const isDark = () => document.documentElement.getAttribute('data-bs-theme') === 'dark'
    || document.body.getAttribute('data-layout-mode') === 'dark';

/**
 * Amount per day as columns with the average operation of the day as a thin dashed line, on one scale.
 * Values are read on hover, not printed on every point: a month has 30 of them and a phone has no room.
 * The arrays may still be empty and filled before render; granularity is asked when labels are drawn.
 */
export function activityChartOptions({ days, bars, line, title, barLabel, lineLabel, events, granularity }) {
    const detect = granularity || (() => 'day');
    const dark = isDark();
    const styles = getComputedStyle(document.body);
    const text = styles.getPropertyValue('--bs-body-color').trim() || (dark ? '#a6b0cf' : '#495057');
    const grid = styles.getPropertyValue('--bs-border-color').trim() || (dark ? '#32394e' : '#eff2f7');

    return {
        series: [
            { name: barLabel, type: 'column', data: bars },
            { name: lineLabel, type: 'line', data: line }
        ],
        chart: {
            height: '100%',
            type: 'line',
            ...locales(),
            foreColor: text,
            background: 'transparent',
            toolbar: { show: true, tools: { download: true, selection: false, zoom: true, zoomin: false, zoomout: false, pan: false, reset: true } },
            events: events || {}
        },
        theme: { mode: dark ? 'dark' : 'light' },
        colors: [dark ? '#6b82ea' : '#556ee6', dark ? '#a6b0cf' : '#74788d'],
        plotOptions: { bar: { columnWidth: '70%', borderRadius: 2, borderRadiusApplication: 'end' } },
        stroke: { width: [0, 2], dashArray: [0, 4], curve: 'straight' },
        markers: { size: 0, hover: { size: 4 } },
        title: { text: title, style: { fontSize: '12px', fontWeight: 500 } },
        dataLabels: { enabled: false },
        grid: { borderColor: grid, strokeDashArray: 3, padding: { left: 4, right: 4 } },
        legend: { position: 'bottom', fontSize: '12px', itemMargin: { horizontal: 6 } },
        tooltip: {
            theme: dark ? 'dark' : 'light',
            shared: true,
            intersect: false,
            x: { formatter: value => formatChartDate(value, detect()) },
            y: { formatter: value => formatChartAmount(value) }
        },
        labels: days,
        xaxis: {
            type: 'datetime',
            axisTicks: { show: false },
            tooltip: { enabled: false },
            labels: {
                rotate: 0,
                hideOverlappingLabels: true,
                style: { fontSize: '11px' },
                formatter: (value, timestamp) => formatChartDate(timestamp ?? value, detect())
            }
        },
        yaxis: {
            min: 0,
            tickAmount: 4,
            forceNiceScale: true,
            labels: { style: { fontSize: '11px' }, formatter: value => kFormatter(value) }
        },
        responsive: [{
            // a phone: no toolbar, fewer grid lines
            breakpoint: 768,
            options: {
                chart: { toolbar: { show: false } },
                yaxis: { min: 0, tickAmount: 3, forceNiceScale: true, labels: { style: { fontSize: '10px' }, formatter: value => kFormatter(value) } }
            }
        }]
    };
}

export function categoriesDonutOptions({ labels, series, totalLabel }) {
    return {
        series,
        labels,
        chart: { type: 'donut', height: 380, ...locales() },
        legend: {
            position: 'bottom',
            fontSize: '11px',
            horizontalAlign: 'center',
            itemMargin: { horizontal: 5, vertical: 2 }
        },
        plotOptions: {
            pie: {
                donut: {
                    size: '65%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: totalLabel || 'Total',
                            formatter: w => formatChartAmount(w.globals.seriesTotals.reduce((a, b) => a + b, 0))
                        }
                    }
                }
            }
        },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: val => formatChartAmount(val) } }
    };
}

export function tagsBarOptions({ categories, data, amountLabel }) {
    return {
        series: [{ name: amountLabel || 'Amount', data }],
        chart: { type: 'bar', height: 380, toolbar: { show: false }, ...locales() },
        plotOptions: {
            bar: { horizontal: true, borderRadius: 4, barHeight: '65%', dataLabels: { position: 'top' } }
        },
        colors: ['#556ee6'],
        dataLabels: {
            enabled: true,
            formatter: val => kFormatter(val),
            offsetX: 25,
            style: { fontSize: '11px', colors: ['#74788d'] }
        },
        xaxis: { categories, labels: { formatter: val => kFormatter(val) } },
        yaxis: { labels: { maxWidth: 130, style: { fontSize: '11px' } } },
        grid: { borderColor: '#f1f1f1' },
        tooltip: { y: { formatter: val => formatChartAmount(val) } }
    };
}
